import React, { useEffect } from 'react';
import {
  HttpError,
  IResourceComponentsProps,
  usePermissions,
  useNavigation,
  useNotification,
  useTranslate,
} from '@pankod/refine-core';
import {
  Backdrop,
  Box,
  CircularProgress,
  Create,
  FormControl,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@pankod/refine-mui';
import { useStepsForm } from '@pankod/refine-react-hook-form';
import {
  FirebaseOptions,
  getApps,
  initializeApp,
} from 'firebase/app';
import {
  createUserWithEmailAndPassword,
  deleteUser,
  getAuth,
  signOut,
} from 'firebase/auth';
import { firebaseConfig as overlayFirebaseConfig } from 'lib/firebaseService';
import { livenowFirebaseConfig } from 'lib/livenowFirebaseService';
import {
  ADMIN_USERS_RESOURCE,
  getAdminUserByEmail,
  getCurrentAdminUser,
} from 'services/adminUserService';
import { getIsolatedAuth } from 'services/authService';
import { CONST } from 'config/const';

type IManagerRecord = {
  email: string;
  role: 'admin' | 'manager';
  offer_limit: number;
  auth_uid: string;
  livenow_auth_uid: string;
  created_at: Date;
  updated_at: Date;
};

export const ManagerCreate: React.FC<
  IResourceComponentsProps
> = () => {
  const t = useTranslate();
  const { push } = useNavigation();
  const notification = useNotification();

  const {
    refineCore: { onFinish, formLoading },
    register,
    setError,
    getValues,
    formState: { errors },
  } = useStepsForm<any, HttpError, any>({
    stepsProps: {
      isBackValidate: false,
    },
    warnWhenUnsavedChanges: true,
    defaultValues: {
      role: 'manager',
      offer_limit: 0,
    },
    refineCoreProps: {
      redirect: false,
    },
  });

  const handleSubmit = async () => {
    const email = (getValues('email') || '').trim();
    const password = getValues('password');
    const role = getValues('role');
    const codeCount = Number(getValues('offer_limit'));

    const overlayAuth = getIsolatedAuth(
      'manager-overlay',
      overlayFirebaseConfig,
    );
    const livenowAuth = getIsolatedAuth(
      'manager-livenow',
      livenowFirebaseConfig,
    );

    const existingOverlayUser = await getAdminUserByEmail(email);

    if (existingOverlayUser) {
      setError('email', {
        type: 'manual',
        message: 'Email already exists in overlay auth',
      });
      return;
    }
    try {
      const overlayCredential = await createUserWithEmailAndPassword(
        overlayAuth,
        email,
        password,
      );

      let livenowCredential;
      try {
        livenowCredential = await createUserWithEmailAndPassword(
          livenowAuth,
          email,
          password,
        );
      } catch (createLivenowError) {
        await deleteUser(overlayCredential.user).catch(
          () => undefined,
        );
        throw createLivenowError;
      }

      const now = new Date();
      await onFinish({
        email: email.toLowerCase(),
        role,
        offer_limit: codeCount,
        status: CONST.ADMIN_STATUS.ACTIVE,
        auth_uid: overlayCredential.user.uid,
        livenow_auth_uid: livenowCredential.user.uid,
        created_at: now,
        updated_at: now,
      });

      await signOut(overlayAuth);
      await signOut(livenowAuth);

      notification.open?.({
        type: 'success',
        message: 'Admin user created successfully',
      });

      push('/admin_users');
    } catch (error) {
      notification.open?.({
        type: 'error',
        message:
          (error as Error)?.message || 'Failed to create manager',
      });
    }
  };

  return (
    <Create
      resource={ADMIN_USERS_RESOURCE}
      title={<Typography variant="h5">Create Admin User</Typography>}
      isLoading={formLoading}
      saveButtonProps={{ onClick: handleSubmit }}
    >
      <Box
        component="form"
        sx={{
          display: 'flex',
          flexDirection: 'column',
        }}
        autoComplete="off"
      >
        <br />
        <Grid
          container
          sx={{
            marginX: { xs: '0px' },
          }}
        >
          <Grid item xs={12}>
            <Grid container>
              <Grid item paddingX={4} xs={12}>
                <Stack gap="24px">
                  <FormControl>
                    <TextField
                      {...register('email', {
                        required: t('errors.required.field', {
                          field: 'email',
                        }),
                      })}
                      size="medium"
                      variant="outlined"
                      required
                      type="email"
                      label="Email (required)"
                    />
                    {errors.email && (
                      <FormHelperText error>
                        {String(errors.email.message || '')}
                      </FormHelperText>
                    )}
                  </FormControl>

                  <FormControl>
                    <TextField
                      {...register('password', {
                        required: t('errors.required.field', {
                          field: 'password',
                        }),
                        minLength: {
                          value: 6,
                          message:
                            'Password must be at least 6 characters',
                        },
                      })}
                      size="medium"
                      variant="outlined"
                      required
                      type="password"
                      label="Password (required)"
                    />
                    {errors.password && (
                      <FormHelperText error>
                        {String(errors.password.message || '')}
                      </FormHelperText>
                    )}
                  </FormControl>

                  <FormControl>
                    <InputLabel id="role">Role</InputLabel>
                    <Select
                      labelId="role"
                      {...register('role', {
                        required: t('errors.required.field', {
                          field: 'role',
                        }),
                      })}
                      size="medium"
                      variant="outlined"
                      label="Role"
                      required
                    >
                      <MenuItem value="manager">Manager</MenuItem>
                      <MenuItem value="admin">Admin</MenuItem>
                    </Select>
                    {errors.role && (
                      <FormHelperText error>
                        {String(errors.role.message || '')}
                      </FormHelperText>
                    )}
                  </FormControl>

                  <FormControl>
                    <TextField
                      {...register('offer_limit', {
                        required: t('errors.required.field', {
                          field: 'offer_limit',
                        }),
                        valueAsNumber: true,
                        min: {
                          value: 0,
                          message:
                            'Offer limit must be greater than or equal to 0',
                        },
                      })}
                      size="medium"
                      variant="outlined"
                      required
                      type="number"
                      label="Offer Limit (required)"
                    />
                    {errors.offer_limit && (
                      <FormHelperText error>
                        {String(errors.offer_limit.message || '')}
                      </FormHelperText>
                    )}
                  </FormControl>
                </Stack>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
        <Backdrop
          sx={{
            color: '#fff',
            zIndex: (theme) => theme.zIndex.drawer + 1,
          }}
          open={formLoading}
        >
          <CircularProgress color="inherit" />
        </Backdrop>
      </Box>
    </Create>
  );
};
